/**
 * Hook customizado para gerenciar notificações/alertas
 * Uso em múltiplos componentes sem duplicação de código
 */

import { useState, useEffect, useRef } from 'react';

export interface UseNotificationReturn {
  message: string;
  isVisible: boolean;
  show: (message?: string, duration?: number) => void;
  hide: () => void;
}

export function useNotification(
  initialMessage: string = '',
  defaultDuration: number = 3000
): UseNotificationReturn {
  const [message, setMessage] = useState(initialMessage);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Exibe a notificação
   * @param customMessage - Mensagem customizada (usa initial se não passou)
   * @param duration - Duração em ms (padrão 3000ms)
   */
  const show = (customMessage?: string, duration: number = defaultDuration) => {
    if (customMessage) {
      setMessage(customMessage);
    }
    setIsVisible(true);

    // Limpa timeout anterior se existir
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Define novo timeout para esconder
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, duration);
  };

  /**
   * Esconde a notificação imediatamente
   */
  const hide = () => {
    setIsVisible(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  /**
   * Cleanup: limpa timeouts quando componente é desmontado
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    message,
    isVisible,
    show,
    hide,
  };
}